"use client";

import { useEffect, useId, useState } from "react";

import Preloader from "@/app/components/preloader";
import { useAppDispatch } from "@/store/hooks";
import { sellerOptionsPageClosed, sellerOptionsPageOpened, type SellerOptionsPage } from "@/store/sellerOptionsSlice";
import { useGetUserQuery } from "@/store/userApi";
import CaretDownIcon from "public/next-public/common/icons/caret-regular-down.svg";
import { FilterPills, NoMatchingSellers, SellerOptionsSort } from "./sellerOptionsControls";
import SellerOptionRow from "./sellerOptionRow";
import { getFilterOrder } from "./sellerOptions";
import { SELLER_OPTIONS_SECTION_ID } from "../sectionIds";
import useAddSellerOptionToCart from "./useAddSellerOptionToCart";
import useSellerOptions, { type SellerOptions } from "./useSellerOptions";
import styles from "./sellerOptionsTable.module.scss";

/** Offers shown before "Show All Sellers"; the next one peeks out faded beneath them. */
const COLLAPSED_OFFER_COUNT = 8;

/**
 * Every offer for the product, with filters and sort when there's more than one. Shown on desktop and tablet;
 * it also hands the offers to the store, where the seller options drawer and the buy box read them.
 */
export default function SellerOptionsTable({ renderedPage }: SellerOptionsTableProps) {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(sellerOptionsPageOpened(renderedPage));
  }, [dispatch, renderedPage]);
  useEffect(() => {
    return () => {
      dispatch(sellerOptionsPageClosed());
    };
  }, [dispatch]);

  const sellerOptions = useSellerOptions(renderedPage);
  const isSignedIn = useIsSignedIn();
  const [expanded, setExpanded] = useState(false);
  const headingId = useId();
  const listId = useId();

  if (sellerOptions === null || sellerOptions.offers.length === 0) {
    return null;
  }

  const { offers, visibleOffers, filters, sort, setSort, clearFilters, isUpdating } = sellerOptions;
  const hasChoices = offers.length > 1;
  const canCollapse = visibleOffers.length > COLLAPSED_OFFER_COUNT;
  const shownOffers = canCollapse && !expanded ? visibleOffers.slice(0, COLLAPSED_OFFER_COUNT) : visibleOffers;
  const peekOffer = canCollapse && !expanded ? visibleOffers[COLLAPSED_OFFER_COUNT] : undefined;

  return (
    <section
      id={SELLER_OPTIONS_SECTION_ID}
      className={styles.SellerOptionsTable}
      aria-labelledby={headingId}
      tabIndex={-1}
      data-seller-options-table=""
    >
      <h2 id={headingId} className={styles.heading}>
        All Seller Options
      </h2>
      {hasChoices && (
        <div className={styles.controls}>
          <FilterPills sellerOptions={sellerOptions} filterOrder={getFilterOrder(isSignedIn)} />
          <SellerOptionsSort sort={sort} onChange={setSort} disabled={visibleOffers.length === 0} />
        </div>
      )}
      <ResultCount count={visibleOffers.length} filtered={filters.size > 0} />
      {visibleOffers.length === 0 ? (
        <NoMatchingSellers onClearFilters={clearFilters} />
      ) : (
        <div className={styles.list} aria-busy={isUpdating}>
          <div className={styles.columnLabels} aria-hidden>
            <span>Seller</span>
            <span>Price + Shipping</span>
            <span className={styles.totalLabel}>Total</span>
          </div>
          <OfferList id={listId} offers={shownOffers} sellerOptions={sellerOptions} />
          {peekOffer && (
            <div className={styles.peek} aria-hidden inert>
              <OfferList offers={[peekOffer]} sellerOptions={sellerOptions} />
              <div className={styles.peekBlurred}>
                <OfferList offers={[peekOffer]} sellerOptions={sellerOptions} />
              </div>
            </div>
          )}
          {isUpdating && (
            <div className={styles.updating}>
              <Preloader textUnderAnimation="Updating prices..." className={styles.preloader} />
            </div>
          )}
        </div>
      )}
      {canCollapse && (
        <button
          type="button"
          className={styles.showAll}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded(!expanded)}
          data-seller-options-show-all={expanded ? "fewer" : "all"}
        >
          {expanded ? "Show Fewer Sellers" : "Show All Sellers"}
          <CaretDownIcon
            className={`${styles.showAllCaret} ${expanded ? styles.caretUp : ""}`}
            width={24}
            height={24}
            aria-hidden
          />
        </button>
      )}
    </section>
  );
}

/** The offers as rows. Shared by the table and the seller options drawer. */
export function OfferList({ id, offers, sellerOptions, layout = "table" }: OfferListProps) {
  const { page, quantity, offers: offersInRecommendedOrder, context, isUpdating } = sellerOptions;
  const { addToCart, addingVendorProductId, failedVendorProductId } = useAddSellerOptionToCart({
    mpId: page.mpId,
    quantity,
    categoryName: page.categoryName,
  });
  // With a single offer there is nothing to recommend it over.
  const hasChoices = offersInRecommendedOrder.length > 1;
  const recommendedId = hasChoices ? offersInRecommendedOrder[0].vendorProductId : null;

  return (
    <ul id={id} className={styles.offers}>
      {offers.map((offer) => (
        <li
          key={offer.vendorProductId}
          className={styles.offer}
          data-seller-option-row={offer.vendorId}
          data-seller-option-recommended={offer.vendorProductId === recommendedId || undefined}
        >
          <SellerOptionRow
            offer={offer}
            layout={layout}
            quantity={quantity}
            isRecommended={offer.vendorProductId === recommendedId}
            isLowestPrice={hasChoices && offer.isLowestPrice === true}
            purchase={context.vendorPurchases.get(offer.vendorId)}
            productRequiresLicense={page.productRequiresLicense}
            isAddingToCart={addingVendorProductId === offer.vendorProductId}
            addToCartFailed={failedVendorProductId === offer.vendorProductId}
            isUpdating={isUpdating}
            onAddToCart={addToCart}
          />
        </li>
      ))}
    </ul>
  );
}

/** Announces how many sellers match after a filter changes. */
export function ResultCount({ count, filtered }: { count: number; filtered: boolean }) {
  return (
    <p className={styles.srOnly} aria-live="polite">
      {filtered ? `${count} ${count === 1 ? "seller matches" : "sellers match"} your filters` : ""}
    </p>
  );
}

/** "Purchased From Before" is offered to signed-in shoppers only. */
export function useIsSignedIn(): boolean {
  const { data: user } = useGetUserQuery();
  return user?.IsGuestUser === false && user.isAuthorized === true;
}

interface SellerOptionsTableProps {
  renderedPage: SellerOptionsPage;
}

interface OfferListProps {
  id?: string;
  offers: VendorOptionState[];
  sellerOptions: SellerOptions;
  layout?: "table" | "drawer";
}
